import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Instance } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetFooter,
  BottomSheetTitle,
  BottomSheetDescription,
} from '@/components/ui/bottom-sheet';
import { formatDate, cn } from '@/lib/utils';
import { Play, Square, RefreshCw, FileText, Heart, Box, ChevronDown, ChevronRight, MoreVertical } from 'lucide-react';

export function Instances() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [actionSheet, setActionSheet] = useState<{ instance: Instance; action: 'stop' | 'restart' } | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['instances', { status: statusFilter }],
    queryFn: () => adminApi.getInstances({ status: statusFilter, limit: 100 }),
  });

  const restartMutation = useMutation({
    mutationFn: (id: string) => adminApi.restartInstance(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      setActionSheet(null);
    },
  });

  const stopMutation = useMutation({
    mutationFn: (id: string) => adminApi.stopInstance(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      setActionSheet(null);
    },
  });

  const healthCheckMutation = useMutation({
    mutationFn: (id: string) => adminApi.checkInstanceHealth(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  const handleViewLogs = async (instance: Instance) => {
    setSelectedInstance(instance);
    setLogsDialogOpen(true);
    try {
      const result = await adminApi.getInstanceLogs(instance.id, 200);
      setLogs(result.logs);
    } catch (error) {
      setLogs('Failed to load logs');
    }
  };

  const confirmAction = () => {
    if (!actionSheet) return;
    if (actionSheet.action === 'stop') {
      stopMutation.mutate(actionSheet.instance.id);
    } else {
      restartMutation.mutate(actionSheet.instance.id);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Instances</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your bot instances</p>
        </div>
        <button 
          onClick={() => refetch()} 
          className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all"
        >
          <RefreshCw className={cn("h-5 w-5 text-muted-foreground", isLoading && "animate-spin")} />
        </button>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {[
          { value: undefined, label: 'All' },
          { value: 'running', label: 'Running' },
          { value: 'stopped', label: 'Stopped' },
          { value: 'error', label: 'Error' },
        ].map((filter) => (
          <button
            key={filter.label}
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-95",
              statusFilter === filter.value
                ? "bg-foreground text-background"
                : "bg-secondary text-muted-foreground"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
        </div>
      ) : data?.instances.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Box className="h-14 w-14 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No instances found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Server</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Container</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Last Health</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.instances.map((instance: Instance) => (
                  <TableRow key={instance.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{instance.customer?.email || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground font-mono">{instance.id.slice(-8)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {instance.server?.name || instance.containerHost || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={instance.status} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {instance.containerId?.slice(0, 12) || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {instance.lastHealthCheck ? formatDate(instance.lastHealthCheck) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon-sm" onClick={() => healthCheckMutation.mutate(instance.id)}>
                          <Heart className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon-sm" onClick={() => handleViewLogs(instance)}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon-sm" onClick={() => setActionSheet({ instance, action: 'restart' })}>
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        {instance.status === 'running' ? (
                          <Button variant="ghost" size="icon-sm" onClick={() => setActionSheet({ instance, action: 'stop' })}>
                            <Square className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon-sm" onClick={() => setActionSheet({ instance, action: 'restart' })}>
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {data?.instances.map((instance: Instance) => (
              <InstanceCard
                key={instance.id}
                instance={instance}
                expanded={expandedId === instance.id}
                onToggle={() => setExpandedId(expandedId === instance.id ? null : instance.id)}
                onAction={(action) => setActionSheet({ instance, action })}
                onViewLogs={() => handleViewLogs(instance)}
                onHealthCheck={() => healthCheckMutation.mutate(instance.id)}
              />
            ))}
          </div>
        </>
      )}

      {data && (
        <p className="text-xs text-muted-foreground text-center">
          {data.instances.length} of {data.total} instances
        </p>
      )}

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Logs: {selectedInstance?.containerName || selectedInstance?.id.slice(-8)}
            </DialogTitle>
          </DialogHeader>
          <pre className="bg-background rounded-xl p-4 overflow-auto max-h-[60vh] text-xs font-mono border border-border">
            {logs || 'Loading...'}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Bottom Sheet */}
      <BottomSheet open={!!actionSheet} onOpenChange={() => setActionSheet(null)}>
        <BottomSheetContent>
          <BottomSheetHeader>
            <BottomSheetTitle>
              {actionSheet?.action === 'stop' ? 'Stop Instance' : 'Restart Instance'}
            </BottomSheetTitle>
            <BottomSheetDescription>
              {actionSheet?.action === 'stop' 
                ? 'This will stop the instance. The customer will not be able to use it until it\'s started again.'
                : 'This will restart the instance. There may be a brief interruption.'}
            </BottomSheetDescription>
          </BottomSheetHeader>
          <BottomSheetFooter>
            <Button
              size="xl"
              variant={actionSheet?.action === 'stop' ? 'destructive' : 'default'}
              onClick={confirmAction}
              disabled={stopMutation.isPending || restartMutation.isPending}
              className="w-full"
            >
              {stopMutation.isPending || restartMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : actionSheet?.action === 'stop' ? (
                <>
                  <Square className="h-4 w-4 mr-2" />
                  Stop Instance
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Restart Instance
                </>
              )}
            </Button>
            <Button
              size="xl"
              variant="secondary"
              onClick={() => setActionSheet(null)}
              className="w-full"
            >
              Cancel
            </Button>
          </BottomSheetFooter>
        </BottomSheetContent>
      </BottomSheet>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant = {
    running: 'success',
    stopped: 'secondary',
    error: 'destructive',
    pending: 'warning',
  }[status] || 'secondary';

  return (
    <div className="flex items-center gap-2">
      <div className={`status-dot status-dot-${status}`} />
      <Badge variant={variant as any}>{status}</Badge>
    </div>
  );
}

interface InstanceCardProps {
  instance: Instance;
  expanded: boolean;
  onToggle: () => void;
  onAction: (action: 'stop' | 'restart') => void;
  onViewLogs: () => void;
  onHealthCheck: () => void;
}

function InstanceCard({ instance, expanded, onToggle, onAction, onViewLogs, onHealthCheck }: InstanceCardProps) {
  return (
    <div className="mobile-card">
      {/* Main row - always visible */}
      <div className="flex items-center gap-3" onClick={onToggle}>
        <div className={`status-dot status-dot-${instance.status}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">
            {instance.customer?.email || `Customer ${instance.customerId.slice(-8)}`}
          </p>
          <p className="text-xs text-muted-foreground">
            {instance.server?.name || 'No server'} · {instance.id.slice(-8)}
          </p>
        </div>
        <ChevronRight className={cn(
          "h-5 w-5 text-muted-foreground transition-transform shrink-0",
          expanded && "rotate-90"
        )} />
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-border space-y-4">
          {/* Details */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Container</p>
              <p className="font-mono text-xs">{instance.containerId?.slice(0, 12) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Last Health</p>
              <p className="text-xs">{instance.lastHealthCheck ? formatDate(instance.lastHealthCheck) : 'Never'}</p>
            </div>
          </div>

          {/* Action buttons - Large touch targets */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="lg"
              variant="secondary"
              onClick={(e) => { e.stopPropagation(); onHealthCheck(); }}
              className="w-full"
            >
              <Heart className="h-4 w-4 mr-2" />
              Health
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={(e) => { e.stopPropagation(); onViewLogs(); }}
              className="w-full"
            >
              <FileText className="h-4 w-4 mr-2" />
              Logs
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="lg"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onAction('restart'); }}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Restart
            </Button>
            {instance.status === 'running' ? (
              <Button
                size="lg"
                variant="destructive"
                onClick={(e) => { e.stopPropagation(); onAction('stop'); }}
                className="w-full"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop
              </Button>
            ) : (
              <Button
                size="lg"
                variant="default"
                onClick={(e) => { e.stopPropagation(); onAction('restart'); }}
                className="w-full"
              >
                <Play className="h-4 w-4 mr-2" />
                Start
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
